import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Sparkles, X, Send, Maximize2, RotateCcw } from 'lucide-react';
import { useAIChat } from '../hooks/useAIChat';
import { AIActionCard } from './AIActionCard';

const QUICK_PROMPTS = [
  'What should I focus on today?',
  'Summarize my pending shipments',
  'Job 047 cleared customs today',
];

export const AIChatWidget = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const { messages, streaming, error, sendMessage, newChat, confirmActionMessage, cancelActionMessage } = useAIChat();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Hide bubble on the dedicated AI page
  if (location.pathname === '/ai-assistant') return null;

  const handleSend = (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setInput('');
    sendMessage(msg);
  };

  return (
    <>
      {!open && (
        <button
          data-testid="ai-chat-bubble"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-110"
          style={{ background: 'linear-gradient(135deg, #2E5E9E, #D4AF37)' }}
          title="Gilron AI Assistant"
        >
          <Sparkles size={24} className="text-white" />
        </button>
      )}

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-6 right-6 z-[60] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
          style={{ width: 'min(400px, calc(100vw - 32px))', height: 'min(600px, calc(100vh - 48px))', background: '#0B1120', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2E5E9E, #D4AF37)' }}>
                <Sparkles size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-white leading-tight">Gilron AI</p>
                <p className="text-[11px]" style={{ color: '#64748B' }}>Work assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="ai-widget-new-chat" onClick={newChat} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="New chat" style={{ color: '#94A3B8' }}>
                <RotateCcw size={15} />
              </button>
              <button data-testid="ai-widget-expand" onClick={() => navigate('/ai-assistant')} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Open full page" style={{ color: '#94A3B8' }}>
                <Maximize2 size={15} />
              </button>
              <button data-testid="ai-widget-close" onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" style={{ color: '#94A3B8' }}>
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="pt-6 text-center">
                <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3" style={{ background: 'rgba(212,175,55,0.15)' }}>
                  <Sparkles size={22} style={{ color: '#E2C15B' }} />
                </div>
                <p className="text-sm font-semibold text-white mb-1">Hi! I&apos;m your work assistant</p>
                <p className="text-xs mb-4" style={{ color: '#64748B' }}>Ask me about shipments, finances, tasks or anything work-related.</p>
                <div className="space-y-2">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      data-testid="ai-widget-quick-prompt"
                      onClick={() => handleSend(p)}
                      className="block w-full text-left text-xs px-3 py-2.5 rounded-xl transition-colors hover:bg-white/10"
                      style={{ background: 'rgba(255,255,255,0.05)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.07)' }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.isActionCard ? (
                  <div className="w-full">
                    <AIActionCard
                      message={m}
                      msgIndex={i}
                      onConfirm={confirmActionMessage}
                      onCancel={cancelActionMessage}
                    />
                  </div>
                ) : (
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === 'user' ? 'rounded-br-md' : 'rounded-bl-md'}`}
                  style={
                    m.role === 'user'
                      ? { background: 'linear-gradient(135deg, #2E5E9E, #C9A227)', color: '#fff' }
                      : { background: 'rgba(255,255,255,0.06)', color: '#E2E8F0', border: '1px solid rgba(255,255,255,0.06)' }
                  }
                >
                  {m.role === 'assistant' ? (
                    m.content ? (
                      <div className="ai-markdown text-[13px]">
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <span className="inline-flex gap-1 items-center py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                    )
                  ) : (
                    m.content
                  )}
                </div>
                )}
              </div>
            ))}
            {error && (
              <p data-testid="ai-widget-error" className="text-xs text-center" style={{ color: '#F87171' }}>{error}</p>
            )}
          </div>

          {/* Input */}
          <div className="px-3 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center gap-2">
              <input
                data-testid="ai-widget-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Ask anything..."
                className="flex-1 text-sm px-3.5 py-2.5 rounded-xl outline-none placeholder:text-slate-500"
                style={{ background: 'rgba(255,255,255,0.06)', color: '#F1F5F9', border: '1px solid rgba(255,255,255,0.08)' }}
              />
              <button
                data-testid="ai-widget-send-btn"
                onClick={() => handleSend()}
                disabled={streaming || !input.trim()}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-opacity disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #2E5E9E, #C9A227)' }}
              >
                <Send size={16} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
